import { useEffect, useState, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Bug,
  Headset,
  Layers,
  Rocket,
  Sparkles,
  Wrench,
} from 'lucide-react'
import { AppChrome } from '../components/AppChrome'
import { useAuth } from '../lib/auth'

type Mode = {
  id: string
  label: string
  charter?: string
  mission_type?: string
  primary_teams?: string[]
}

type Dashboard = {
  thesis?: string
  complete_sdlc?: string[]
  engagement_count?: number
  support_draft_count?: number
  team_count?: number
  by_mode?: Record<string, number>
  catalog?: Mode[]
  engagements?: {
    engagement_id: string
    shop_mode?: string
    title?: string
    status?: string
    client_name?: string
    mission_type?: string
  }[]
  csat_policy?: {
    auto_zendesk_write?: boolean
    human_publish_required?: boolean
    goal?: string
  }
  ops?: {
    csat?: {
      avg_csat?: number | null
      published_attested?: number
      avg_draft_to_publish_hours?: number | null
    }
    velocity?: {
      by_mode?: Record<string, { avg_lead_hours?: number | null; shipped?: number }>
      backlog_aging?: Record<string, number>
    }
    economics?: { total_billable_hours?: number; time_entries?: number }
    portfolio?: { client_count?: number }
    calibration?: { samples?: number }
  }
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  })
  if (!res.ok) throw new Error(await res.text())
  return res.json() as Promise<T>
}

const MODE_ICONS: Record<string, ReactNode> = {
  new_software: <Rocket className="h-5 w-5 text-[var(--color-cyan)]" />,
  enhance_existing: <Layers className="h-5 w-5 text-[var(--color-amber)]" />,
  bug_fix: <Bug className="h-5 w-5 text-[var(--color-rose)]" />,
  support_csat: <Headset className="h-5 w-5 text-[var(--color-cyan)]" />,
}

export function ShopPage() {
  const { user, loading } = useAuth()
  const [dash, setDash] = useState<Dashboard | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [title, setTitle] = useState('')
  const [mode, setMode] = useState('new_software')
  const [repoUrl, setRepoUrl] = useState('')
  const [clientName, setClientName] = useState('')

  async function load() {
    setBusy(true)
    setError(null)
    try {
      setDash(await api<Dashboard>('/api/afos360/v1/shop'))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load shop OS')
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    if (user) void load()
  }, [user])

  if (!loading && !user) return <Navigate to="/signin" replace />

  async function createEngagement() {
    if (!title.trim()) return
    setBusy(true)
    setError(null)
    try {
      const eng = await api<{ engagement_id: string; shop_mode?: string }>(
        '/api/afos360/v1/shop/engagements',
        {
          method: 'POST',
          body: JSON.stringify({
            engagement_id: mode,
            title,
            client_name: clientName || null,
            repo_url: repoUrl || null,
            auto_start_mission: mode !== 'support_csat',
            brief: `${mode} engagement for software shop`,
          }),
        },
      )
      setMessage(`Opened ${eng.shop_mode} engagement ${eng.engagement_id}`)
      setTitle('')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed')
    } finally {
      setBusy(false)
    }
  }

  async function draftZendesk() {
    setBusy(true)
    setError(null)
    try {
      const result = await api<{ pulled: number; note?: string }>(
        '/api/afos360/v1/shop/support/zendesk-drafts',
        { method: 'POST', body: JSON.stringify({ limit: 5 }) },
      )
      setMessage(
        `Drafted CSAT replies for ${result.pulled} Zendesk ticket(s). ${result.note || ''}`,
      )
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Zendesk draft failed')
    } finally {
      setBusy(false)
    }
  }

  async function seedOpsDemo() {
    setBusy(true)
    setError(null)
    try {
      const seeded = await api<{ harvested?: number; client?: { name?: string } }>(
        '/api/afos360/v1/shop/seed-demo',
        { method: 'POST', body: '{}' },
      )
      setMessage(
        `Seeded closed-loop ops for ${seeded.client?.name || 'demo client'} · harvested ${seeded.harvested ?? 0} skill proposals`,
      )
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Seed failed')
    } finally {
      setBusy(false)
    }
  }

  async function runLeverageSlice() {
    setBusy(true)
    setError(null)
    try {
      const slice = await api<{
        build_id?: string
        ci_green?: boolean
        adversarial_qa?: { verdict?: string }
      }>('/api/afos360/v1/leverage/slice', {
        method: 'POST',
        body: JSON.stringify({
          brief: title.trim() || 'Shop leverage delivery slice',
          mission_type: mode === 'bug_fix' ? 'bug_fix' : 'feature',
        }),
      })
      setMessage(
        `Leverage slice ${slice.build_id}: CI=${String(slice.ci_green)} · QA=${slice.adversarial_qa?.verdict || 'n/a'}`,
      )
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Leverage slice failed')
    } finally {
      setBusy(false)
    }
  }

  async function createClient() {
    if (!clientName.trim()) return
    setBusy(true)
    setError(null)
    try {
      const c = await api<{ client_id: string; name: string }>(
        '/api/afos360/v1/shop/clients',
        {
          method: 'POST',
          body: JSON.stringify({
            name: clientName,
            retainer_hours_month: 40,
          }),
        },
      )
      setMessage(`Client ${c.name} (${c.client_id}) ready`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Client create failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AppChrome>
      <main className="afos-page space-y-8">
        <header>
          <h1 className="afos-page-title">Software shop OS</h1>
          <p className="afos-page-lead">
            Complete SDLC for shops: new software, enhance existing systems, bug fix for
            clients with legacy apps, and Zendesk support agents that lift CSAT.
          </p>
        </header>

        {dash?.thesis && (
          <div className="glass rounded-3xl p-5 text-sm text-[var(--color-mist)]">
            {dash.thesis}
          </div>
        )}

        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            icon={<Sparkles className="h-5 w-5 text-[var(--color-cyan)]" />}
            label="Engagements"
            value={String(dash?.engagement_count ?? '—')}
          />
          <Stat
            icon={<Headset className="h-5 w-5 text-[var(--color-amber)]" />}
            label="Avg CSAT"
            value={
              dash?.ops?.csat?.avg_csat != null
                ? String(dash.ops.csat.avg_csat)
                : String(dash?.support_draft_count ?? '—')
            }
          />
          <Stat
            icon={<Wrench className="h-5 w-5 text-[var(--color-cyan)]" />}
            label="Clients"
            value={String(dash?.ops?.portfolio?.client_count ?? '—')}
          />
          <Stat
            icon={<Bug className="h-5 w-5 text-[var(--color-rose)]" />}
            label="Billable hrs"
            value={String(dash?.ops?.economics?.total_billable_hours ?? 0)}
          />
        </section>

        {dash?.ops?.velocity && (
          <div className="glass rounded-3xl p-4 text-sm text-[var(--color-mist)]">
            <div className="mb-2 font-[family-name:var(--font-display)] text-lg font-bold text-[var(--color-foam)]">
              Velocity · backlog aging
            </div>
            <div className="flex flex-wrap gap-3 font-[family-name:var(--font-mono)] text-[11px]">
              {Object.entries(dash.ops.velocity.by_mode || {}).map(([m, v]) => (
                <span key={m}>
                  {m}: shipped {v.shipped ?? 0}
                  {v.avg_lead_hours != null ? ` · ${v.avg_lead_hours}h lead` : ''}
                </span>
              ))}
              {dash.ops.velocity.backlog_aging && (
                <span>
                  open aging 7d+: {dash.ops.velocity.backlog_aging['7d_plus'] ?? 0}
                </span>
              )}
            </div>
          </div>
        )}

        {!!dash?.complete_sdlc?.length && (
          <ol className="flex flex-wrap gap-2 text-xs text-[var(--color-mist)]">
            {dash.complete_sdlc.map((step, i) => (
              <li
                key={step}
                className="rounded-full border border-[var(--color-line)] bg-black/20 px-3 py-1"
              >
                {i + 1}. {step}
              </li>
            ))}
          </ol>
        )}

        <section className="grid gap-4 md:grid-cols-2">
          {(dash?.catalog || []).map((m) => (
            <motion.button
              key={m.id}
              type="button"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => setMode(m.id)}
              className={`glass rounded-3xl p-4 text-left ${
                mode === m.id ? 'ring-1 ring-[var(--color-cyan)]' : ''
              }`}
            >
              <div className="flex items-center gap-2">
                {MODE_ICONS[m.id] || <Sparkles className="h-5 w-5" />}
                <span className="font-[family-name:var(--font-display)] text-lg font-bold text-[var(--color-foam)]">
                  {m.label}
                </span>
              </div>
              <p className="mt-2 text-sm text-[var(--color-mist)] line-clamp-3">
                {m.charter}
              </p>
              <div className="mt-2 font-[family-name:var(--font-mono)] text-[10px] text-[var(--color-mist)]">
                mission={m.mission_type} · teams={(m.primary_teams || []).slice(0, 3).join(', ')}
              </div>
            </motion.button>
          ))}
        </section>

        <div className="flex flex-col gap-3 rounded-3xl border border-[var(--color-line)] bg-black/25 p-4 sm:flex-row sm:flex-wrap sm:items-end">
          <label className="flex min-w-[14rem] flex-1 flex-col gap-1 text-xs text-[var(--color-mist)]">
            Title
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="rounded-2xl border border-[var(--color-line)] bg-black/30 px-3 py-2 text-sm text-[var(--color-foam)]"
              placeholder="Client engagement title"
            />
          </label>
          <label className="flex min-w-[10rem] flex-col gap-1 text-xs text-[var(--color-mist)]">
            Client
            <input
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              className="rounded-2xl border border-[var(--color-line)] bg-black/30 px-3 py-2 text-sm text-[var(--color-foam)]"
              placeholder="Acme Shop client"
            />
          </label>
          <label className="flex min-w-[14rem] flex-1 flex-col gap-1 text-xs text-[var(--color-mist)]">
            Existing repo URL (brownfield / bug)
            <input
              value={repoUrl}
              onChange={(e) => setRepoUrl(e.target.value)}
              className="rounded-2xl border border-[var(--color-line)] bg-black/30 px-3 py-2 text-sm text-[var(--color-foam)]"
              placeholder="https://github.com/client/app"
            />
          </label>
          <button
            type="button"
            disabled={busy || !title.trim()}
            onClick={() => void createEngagement()}
            className="rounded-full bg-[var(--color-cyan)] px-4 py-2 text-sm font-semibold text-[var(--color-void)]"
          >
            Start {mode.replace('_', ' ')}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void draftZendesk()}
            className="rounded-full border border-[var(--color-line)] px-4 py-2 text-sm text-[var(--color-foam)]"
          >
            Draft Zendesk CSAT replies
          </button>
          <button
            type="button"
            disabled={busy || !clientName.trim()}
            onClick={() => void createClient()}
            className="rounded-full border border-[var(--color-line)] px-4 py-2 text-sm text-[var(--color-foam)]"
          >
            Register client
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void seedOpsDemo()}
            className="rounded-full border border-[var(--color-amber)]/40 px-4 py-2 text-sm text-[var(--color-amber)]"
          >
            Seed ops demo (1–8)
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void runLeverageSlice()}
            className="rounded-full border border-[var(--color-cyan)]/40 px-4 py-2 text-sm text-[var(--color-cyan)]"
          >
            Run 1k-dev leverage slice
          </button>
        </div>

        {error && (
          <div className="rounded-2xl border border-[rgba(255,107,138,0.4)] bg-[rgba(255,107,138,0.1)] px-4 py-3 text-sm text-[var(--color-rose)]">
            {error}
          </div>
        )}
        {message && (
          <div className="rounded-2xl border border-[rgba(46,230,197,0.35)] bg-[rgba(46,230,197,0.08)] px-4 py-3 text-sm text-[var(--color-cyan)]">
            {message}
          </div>
        )}

        <section className="space-y-3">
          <h2 className="font-[family-name:var(--font-display)] text-2xl font-extrabold">
            Active engagements
          </h2>
          <p className="text-sm text-[var(--color-mist)]">
            CSAT policy: human publish required · auto Zendesk write ={' '}
            {String(dash?.csat_policy?.auto_zendesk_write ?? false)}
          </p>
          <ul className="space-y-2">
            {(dash?.engagements || []).map((e) => (
              <li
                key={e.engagement_id}
                className="rounded-2xl border border-[var(--color-line)] bg-black/25 px-4 py-3"
              >
                <div className="font-semibold text-[var(--color-foam)]">{e.title}</div>
                <div className="mt-1 font-[family-name:var(--font-mono)] text-[10px] text-[var(--color-mist)]">
                  {e.engagement_id} · {e.shop_mode} · {e.mission_type} · {e.status}
                  {e.client_name ? ` · ${e.client_name}` : ''}
                </div>
              </li>
            ))}
            {!dash?.engagements?.length && (
              <p className="text-sm text-[var(--color-mist)]">
                No engagements yet. Start new software, enhance existing, bug fix, or
                draft Zendesk CSAT replies.
              </p>
            )}
          </ul>
        </section>
      </main>
    </AppChrome>
  )
}

function Stat({
  icon,
  label,
  value,
}: {
  icon: ReactNode
  label: string
  value: string
}) {
  return (
    <div className="glass rounded-3xl p-5">
      <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[var(--color-mist)]">
        {icon}
        {label}
      </div>
      <div className="font-[family-name:var(--font-display)] text-3xl font-extrabold text-[var(--color-foam)]">
        {value}
      </div>
    </div>
  )
}
